import { commendaAiAttachmentDownloadUrl } from '@commenda/api/commenda-ai/api';
import type { CommendaAiSavedMessageFeedback } from '@commenda/api/commenda-ai/types';
import { Avatar } from '@commenda/kakashi';
import type { UIMessage } from 'ai';
import { useMemo } from 'react';
import {
  defaultRehypePlugins,
  type AnimateOptions,
  type StreamdownProps,
} from 'streamdown';
import { AccountManagerCard } from './AccountManagerCard';
import { AgentErrorPartBlock } from './AgentErrorPartBlock';
import { AgentPlanPartBlock } from './AgentPlanPartBlock';
import {
  Message,
  MessageContent,
  MessageResponse,
} from './ai-elements/message';
import { AttachmentCard } from './AttachmentCard';
import { AttachmentPillGroups } from './AttachmentPillGroups';
import type { PreviewFile } from './AttachmentPreviewPanel';
import { ChainOfThought } from './ChainOfThought';
import { ChatAttachmentHelper } from './ChatAttachmentHelper';
import { ChatCardHelper, type ChatCard } from './ChatCardHelper';
import { CommendaAiMessageHelper } from './commenda-ai-message-helper';
import { DataPartHelper, type IDataPartEntry } from './data-part-helper';
import { DataRoomUploadPill } from './data-room/DataRoomUploadPill';
import { DataPartBlock } from './DataPartBlock';
import { GeneratedFileHelper } from './generated-file-helper';
import { GeneratedFileAttachmentCard } from './GeneratedFileAttachmentCard';
import { IssueDraftCard } from './IssueDraftCard';
import { rehypeChatDocumentMentions } from './markdown/chat-document-mentions';
import type { IChatDocumentMention } from './markdown/chat-document-mentions.types';
import { MessageActions } from './MessageActions';
import { QuestionBlockHelper, type QuestionAsk } from './question-block';
import { QuestionSegment } from './QuestionSegment';
import { ResearchProcessBlock } from './ResearchProcessBlock';
import { TaxCalculationCard } from './TaxCalculationCard';
import { ToolMessagePart, isToolPart } from './ToolMessagePart';

// Message parts are append-only and never reorder within a message, so a message-scoped positional
// key is stable — wrapped in a helper so it reads as an id, not a raw array index.
const partKey = (messageId: string, index: number) =>
  `${messageId}-part-${index}`;

type MessagePart = UIMessage['parts'][number];
type TextPart = Extract<MessagePart, { type: 'text' }>;
type FilePart = Extract<MessagePart, { type: 'file' }>;

/** One ordered chunk of an assistant message: either a run of *consecutive* thought parts (reasoning
 * + tool calls) that collapse under a single chain-of-thought accordion, or a text answer. Grouping
 * by run — not by type — keeps tool calls made *after* a response in their own accordion instead of
 * clubbing every tool call into one at the top. */
type MessageSegment =
  | {
      kind: 'thoughts';
      key: string;
      parts: { part: MessagePart; index: number }[];
    }
  | { kind: 'text'; key: string; part: TextPart }
  | { kind: 'card'; key: string; card: ChatCard }
  | { kind: 'data'; key: string; partType: string; data: unknown }
  | { kind: 'research-process'; key: string; entries: IDataPartEntry[] }
  | { kind: 'question'; key: string; toolCallId: string; ask: QuestionAsk };

function groupMessageSegments(
  messageId: string,
  parts: MessagePart[]
): MessageSegment[] {
  const segments: MessageSegment[] = [];
  let pendingThoughts: { part: MessagePart; index: number }[] = [];
  let pendingProcess: { entry: IDataPartEntry; index: number }[] = [];

  const flushThoughts = () => {
    if (pendingThoughts.length === 0) return;
    segments.push({
      kind: 'thoughts',
      key: `cot-${messageId}-${pendingThoughts[0].index}`,
      parts: pendingThoughts,
    });
    pendingThoughts = [];
  };

  const flushProcess = () => {
    if (pendingProcess.length === 0) return;
    segments.push({
      kind: 'research-process',
      key: `research-${messageId}-${pendingProcess[0].index}`,
      entries: pendingProcess.map(({ entry }) => entry),
    });
    pendingProcess = [];
  };

  const flushAll = () => {
    flushThoughts();
    flushProcess();
  };

  parts.forEach((part, index) => {
    // A question leaves the accordion: it is the assistant addressing the user, not work it did.
    if (
      part.type === 'dynamic-tool' &&
      part.toolName === QuestionBlockHelper.ASK_USER_QUESTION_TOOL_NAME &&
      part.state !== 'output-error'
    ) {
      const ask = QuestionBlockHelper.parseQuestionAsk(part.input);
      if (ask) {
        flushAll();
        segments.push({
          kind: 'question',
          key: partKey(messageId, index),
          toolCallId: part.toolCallId,
          ask,
        });

        return;
      }
    }

    const card = isToolPart(part) ? ChatCardHelper.forPart(part) : null;
    if (card) {
      // A card answers the user directly, so it leaves the accordion and stands on its own.
      flushAll();
      segments.push({ kind: 'card', key: partKey(messageId, index), card });
    } else if (part.type === 'reasoning' || isToolPart(part)) {
      flushProcess();
      pendingThoughts.push({ part, index });
    } else if (part.type === 'text') {
      flushAll();
      segments.push({ kind: 'text', key: partKey(messageId, index), part });
    } else if (part.type.startsWith('data-')) {
      const entry = {
        partType: part.type,
        data: (part as { data?: unknown }).data,
      };
      const kind = DataPartHelper.getKind(part.type);

      // Hidden parts are already on screen as a card or recap; dropping them keeps a research run's
      // trace in one section across the question cards that interrupt it.
      if (kind === 'hidden') return;
      if (kind === 'research-process') {
        flushThoughts();
        pendingProcess.push({ entry, index });
        return;
      }
      flushAll();
      segments.push({ kind: 'data', key: partKey(messageId, index), ...entry });
    }
    // File parts render in a separate inline row, so they're skipped here.
  });
  flushAll();

  return segments;
}

// Per-word fade as tokens arrive → a smooth "type effect". Gated by isAnimating
// so only the live stream animates; loaded history renders statically.
const RESPONSE_ANIMATION: AnimateOptions = {
  animation: 'fadeIn',
  sep: 'word',
  duration: 250,
  easing: 'cubic-bezier(0.23, 1, 0.32, 1)',
};

interface CommendaAiMessageItemProps {
  conversationId: string;
  message: UIMessage;
  isLastMessage: boolean;
  isGenerating: boolean;
  /** Text of the first user message after this one; null when none exists. Feeds the message
   * position context so question cards can derive live vs answered. */
  followingUserMessageText: string | null;
  signedInUserName: string | null;
  /** The verdict this reader already saved on the message, so the thumb stays filled and the
   * feedback modal reopens with their category and comment. */
  initialFeedback: CommendaAiSavedMessageFeedback | null;
  onFeedbackSaved: () => void;
  /** Present only on the message whose reply can be regenerated (the last one). */
  onRegenerate?: () => void;
  onPreviewFile: (file: PreviewFile) => void;
  /** Document and folder names this conversation's tools returned; their mentions lead with an icon. */
  documentMentions: IChatDocumentMention[];
}

/** One conversation message: the segment rendering extracted from CommendaAiConversation's message
 * map, plus the per-message position provider that question cards read. A component (not inline
 * map JSX) so the context value can memoize per message instead of rebuilding on every token. */
export function CommendaAiMessageItem({
  conversationId,
  message,
  isLastMessage,
  isGenerating,
  followingUserMessageText,
  signedInUserName,
  initialFeedback,
  onFeedbackSaved,
  onRegenerate,
  onPreviewFile,
  documentMentions,
}: CommendaAiMessageItemProps) {
  const rehypePlugins = useMemo<NonNullable<StreamdownProps['rehypePlugins']>>(
    () => [
      ...Object.values(defaultRehypePlugins),
      [rehypeChatDocumentMentions, { mentions: documentMentions }],
    ],
    [documentMentions]
  );

  const fileParts = message.parts.filter(
    (part): part is FilePart => part.type === 'file'
  );

  // A card's answer is already on screen as that card's recap, so its bubble would say it twice.
  // Read off the marker part the sender attaches and the backend stores, never inferred from the
  // message's text. Kept when it carries attachments, which a recap cannot show.
  const isHiddenCardAnswer =
    QuestionBlockHelper.isQuestionAnswerMessage(message.parts) &&
    fileParts.length === 0;

  // Actions sit under a completed assistant reply (not while the last one streams).
  const messageText = message.parts
    .filter((part): part is TextPart => part.type === 'text')
    .map((part) => part.text)
    .join('\n\n');
  const showActions =
    message.role === 'assistant' &&
    messageText.trim().length > 0 &&
    !(isLastMessage && isGenerating);
  // Copy-only action under a user message, aligned to the right of its bubble.
  const isUserMessage = message.role === 'user';
  const showUserActions = isUserMessage && messageText.trim().length > 0;
  // Stored alongside the message; absent on one just sent, which reads as "Just now"
  // and as the signed-in user.
  const { createdAt, senderName, timelineBlocks } =
    CommendaAiMessageHelper.readMetadata(message.metadata);
  const authorName = senderName ?? signedInUserName;

  // Live = the last assistant turn while it's still generating; only its final segment
  // is "thinking"/animating.
  const isLiveAssistant =
    message.role === 'assistant' && isLastMessage && isGenerating;
  const segments = groupMessageSegments(message.id, message.parts);
  // A sent data room sits above the bubble with the other files, not inside it (Figma 3358:1608).
  const dataRooms = isUserMessage
    ? segments.flatMap((segment) => {
        const dataRoom =
          segment.kind === 'data' &&
          DataPartHelper.getKind(segment.partType) === 'document-upload'
            ? DataPartHelper.getDocumentUpload(segment.data)
            : null;
        return dataRoom ? [dataRoom] : [];
      })
    : [];

  // A reply opens before its first part arrives; an empty bubble would still take the list's gap
  // and push the thinking indicator down.
  if (
    isHiddenCardAnswer ||
    (message.role === 'assistant' &&
      segments.length === 0 &&
      fileParts.length === 0)
  )
    return null;

  return (
    <Message
      from={message.role}
      {...(isLastMessage && message.role === 'assistant'
        ? { className: 'gap-xl' }
        : {})}
      {...(isUserMessage ? { className: 'gap-sm' } : {})}
    >
      {isUserMessage && authorName && (
        <Avatar
          className="ml-auto"
          size="xs"
          name={authorName}
          initialsCount={2}
          tooltip={{ content: authorName }}
        />
      )}
      {isUserMessage && fileParts.length > 0 && (
        <AttachmentPillGroups
          align="end"
          items={fileParts.map((part) => ({
            key: part.url,
            fileName: ChatAttachmentHelper.getDisplayName(
              part.filename,
              part.url
            ),
            mimeType: part.mediaType,
            fileRef: part.url,
          }))}
        />
      )}
      {dataRooms.map((dataRoom) => (
        <DataRoomUploadPill key={dataRoom.uploadId} dataRoom={dataRoom} />
      ))}
      <MessageContent>
        {segments.map((segment, segmentIndex) => {
          const isLastSegment = segmentIndex === segments.length - 1;
          if (segment.kind === 'thoughts')
            return (
              <ChainOfThought
                key={segment.key}
                isThinking={isLiveAssistant && isLastSegment}
              >
                {segment.parts.map(({ part, index }) =>
                  part.type === 'reasoning' ? (
                    <p
                      key={partKey(message.id, index)}
                      className="whitespace-pre-wrap text-xs leading-[15px] text-text-quaternary"
                    >
                      {part.text}
                    </p>
                  ) : (
                    <ToolMessagePart
                      key={partKey(message.id, index)}
                      part={part}
                    />
                  )
                )}
              </ChainOfThought>
            );
          if (
            segment.kind === 'card' &&
            segment.card.kind !== 'documentReading'
          )
            return (
              <div key={segment.key} className="mt-md flex flex-col gap-md">
                {segment.card.kind === 'accountManager' ? (
                  segment.card.managers.map((manager) => (
                    <AccountManagerCard
                      key={manager.email}
                      name={manager.name}
                      email={manager.email}
                      bookingLink={manager.bookingLink}
                    />
                  ))
                ) : segment.card.kind === 'issueDraft' ? (
                  <IssueDraftCard
                    subject={segment.card.subject}
                    message={segment.card.message}
                    companyId={segment.card.companyId}
                    destination={segment.card.destination}
                    files={segment.card.files}
                  />
                ) : (
                  <TaxCalculationCard
                    entityName={segment.card.entityName}
                    addressLabel={segment.card.addressLabel}
                    isStreetGiven={segment.card.isStreetGiven}
                    isNoStateSalesTax={segment.card.isNoStateSalesTax}
                    summary={segment.card.summary}
                    calculatorLink={segment.card.calculatorLink}
                  />
                )}
              </div>
            );
          // A reading run floats above the composer instead of standing in the reply.
          if (segment.kind === 'card') return null;
          if (segment.kind === 'question')
            return (
              <QuestionSegment
                key={segment.key}
                toolCallId={segment.toolCallId}
                ask={segment.ask}
                record={QuestionBlockHelper.findQuestionRecord(
                  timelineBlocks,
                  segment.toolCallId
                )}
                isLastAssistantMessage={
                  message.role === 'assistant' && isLastMessage
                }
                hasFollowingReply={followingUserMessageText !== null}
              />
            );
          if (segment.kind === 'research-process')
            return (
              <ResearchProcessBlock
                key={segment.key}
                entries={segment.entries}
              />
            );
          if (segment.kind === 'data') {
            const dataPartKind = DataPartHelper.getKind(segment.partType);
            if (
              isUserMessage &&
              dataPartKind === 'document-upload' &&
              DataPartHelper.getDocumentUpload(segment.data)
            )
              return null;
            if (dataPartKind === 'error')
              return (
                <AgentErrorPartBlock key={segment.key} data={segment.data} />
              );
            if (dataPartKind === 'agent-plan')
              return (
                <AgentPlanPartBlock key={segment.key} data={segment.data} />
              );
            return (
              <DataPartBlock
                key={segment.key}
                partType={segment.partType}
                data={segment.data}
              />
            );
          }
          return (
            <MessageResponse
              key={segment.key}
              animated={RESPONSE_ANIMATION}
              isAnimating={isLiveAssistant && isLastSegment}
              rehypePlugins={rehypePlugins}
            >
              {segment.part.text}
            </MessageResponse>
          );
        })}
        {!isUserMessage && fileParts.length > 0 && (
          <div className="mt-md flex flex-col gap-md">
            {fileParts.map((part) => {
              const generatedFileMetadata =
                GeneratedFileHelper.getMetadata(part);
              if (generatedFileMetadata)
                return (
                  <GeneratedFileAttachmentCard
                    key={part.url}
                    conversationId={conversationId}
                    messageId={message.id}
                    part={part}
                    metadata={generatedFileMetadata}
                    isStreaming={isLastMessage && isGenerating}
                    onPreviewFile={onPreviewFile}
                  />
                );

              const fileName = ChatAttachmentHelper.getDisplayName(
                part.filename,
                part.url
              );
              const downloadUrl = commendaAiAttachmentDownloadUrl(part.url, {
                conversationId,
                messageId: message.id,
              });
              return (
                <AttachmentCard
                  key={part.url}
                  fileName={fileName}
                  mimeType={part.mediaType}
                  fileRef={part.url}
                  downloadUrl={downloadUrl}
                  onPreview={() =>
                    onPreviewFile({
                      fileName,
                      mimeType: part.mediaType,
                      fileRef: part.url,
                      downloadUrl,
                    })
                  }
                />
              );
            })}
          </div>
        )}
      </MessageContent>
      {showActions && (
        <MessageActions
          text={messageText}
          sentAt={createdAt}
          sentAtPosition="trailing"
          onRegenerate={onRegenerate}
          feedbackConfig={{
            conversationId,
            messageId: message.id,
            initialFeedback,
            onRatingSaved: onFeedbackSaved,
          }}
        />
      )}
      {showUserActions && (
        <MessageActions
          text={messageText}
          sentAt={createdAt}
          className="ml-auto"
        />
      )}
    </Message>
  );
}
